import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useAuth } from "../../context/auth-context";
import axiosInstance from "../../utils/axiosConfig";
import { API_URL } from "../../constant";
import { ApexOptions } from "apexcharts";

/** The card this chart sits on — the stacked-segment gap is drawn in it. */
const SURFACE = "#1C2227";
/** Recessive ink for axis labels and the legend. */
const AXIS_INK = "#617889";
const GRID_LINE = "#2B343B";

/**
 * Two categorical hues, validated against the dark surface for colour-vision
 * deficiency (worst-pair ΔE 26 protan / 32 tritan).
 *
 * The previous pale blue (#9DD0FF) sat outside the usable lightness band and
 * under the chroma floor on this surface — it read as washed-out grey rather
 * than as a distinct series.
 */
const SERIES_CRYPTO = "#2385DF";
const SERIES_GIFTCARD = "#D95926";

/** Axis ticks: short enough not to collide. */
const formatCompactCurrency = (value: number) => {
  const amount = Number(value) || 0;
  if (Math.abs(amount) >= 1_000_000)
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}k`;
  return `$${Math.round(amount)}`;
};

/** Tooltips: the exact figure, where there is room for it. */
const formatFullCurrency = (value: number) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const WalletGraph = () => {
  const { isAuthenticated } = useAuth();
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // Get the current month (0-indexed)

  const [cryptoYearlyPayout, setCryptoYearlyPayout] = useState<any[]>([]);
  const [giftcardYearlyPayout, setGiftcardYearlyPayout] = useState<any[]>([]);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];
  const xAxisCategories = months.slice(0, currentMonth + 1);

  useEffect(() => {
    setTimeout(() => {
      axiosInstance
        .get(`${API_URL}api/stats/graph-data/CRYPTO/YEAR?userType=basic-user`)
        .then((r: any) => {
          setCryptoYearlyPayout(r.data.data);
        })
        .catch((e: any) => {
          console.log(e.response.data.message);
        });
    }, 2000);
  }, [isAuthenticated]);
  useEffect(() => {
    setTimeout(() => {
      axiosInstance
        .get(
          `${API_URL}api/stats/graph-data/GIFT-CARD/YEAR?userType=basic-user`
        )
        .then((r: any) => {
          setGiftcardYearlyPayout(r.data.data);
        })
        .catch((e: any) => {
          console.log(e.response.data.message);
        });
    }, 2000);
  }, [isAuthenticated]);

  const returnMonthData = (arr: any[]) => {
    const emptyArr = Array(12).fill(0);

    for (let i = 1; i <= emptyArr.length; i++) {
      const monthItem = arr.find(
        (item: { _id: { month: number } }) => item._id.month === i
      );
      emptyArr[i - 1] = monthItem ? monthItem.total.toFixed(2) : 0;
    }

    return emptyArr;
  };

  const chartData = useMemo(
    () => ({
      options: {
        chart: {
          type: "bar",
          stacked: true,
          background: "transparent",
          fontFamily: "Manrope, sans-serif",
          toolbar: { show: false },
          animations: { easing: "easeinout", speed: 400 },
        },
        plotOptions: {
          bar: {
            // Capped so the bar never fills its slot — the leftover band is air.
            columnWidth: "20%",
            // Rounded at the data end, square where it meets the baseline.
            borderRadius: 4,
            borderRadiusApplication: "end",
            borderRadiusWhenStacked: "last",
          },
        },
        // Not a border: a 2px stroke in the surface color, which reads as a gap
        // separating the two stacked segments.
        stroke: {
          show: true,
          width: 2,
          colors: [SURFACE],
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: "dark",
          y: {
            formatter: (value: any) => formatFullCurrency(value),
          },
        },
        xaxis: {
          type: "category",
          categories: xAxisCategories,
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: "normal",
              colors: AXIS_INK,
            },
          },
          axisBorder: { show: true, color: GRID_LINE },
          axisTicks: { show: false },
          crosshairs: { show: false },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: "normal",
              colors: AXIS_INK,
            },
            formatter: (value: any) => formatCompactCurrency(value),
          },
          // Bars must read from zero. Beyond that the scale is left to
          // ApexCharts: `stepSize: 5` against a max in the hundreds was
          // generating one label every $5 — the stack of unreadable text that
          // ran down the axis. A small tick count is what keeps it legible.
          min: 0,
          tickAmount: 4,
          forceNiceScale: true,
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          offsetY: 6,
          fontSize: "12px",
          fontFamily: "Manrope, sans-serif",
          fontWeight: 500,
          markers: { radius: 3, width: 10, height: 10, offsetX: -3 },
          itemMargin: { horizontal: 10 },
          labels: {
            colors: AXIS_INK,
            useSeriesColors: false,
          },
        },
        grid: {
          show: true,
          borderColor: GRID_LINE,
          strokeDashArray: 4,
          // Horizontal rules only — vertical ones read as bars.
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 4, right: 12, top: 0, bottom: 0 },
        },
        noData: {
          text: "No trade volume yet",
          style: { color: AXIS_INK, fontSize: "12px" },
        },
        responsive: [
          {
            breakpoint: 640,
            options: {
              plotOptions: { bar: { columnWidth: "42%" } },
              legend: { offsetY: 4 },
            },
          },
        ],
      },
      series: [
        {
          name: "Crypto",
          data: returnMonthData(cryptoYearlyPayout).slice(0, currentMonth + 1),
          color: SERIES_CRYPTO,
        },
        {
          name: "Giftcard",
          data: returnMonthData(giftcardYearlyPayout).slice(
            0,
            currentMonth + 1
          ),
          color: SERIES_GIFTCARD,
        },
      ],
    }),
    [cryptoYearlyPayout, currentMonth, giftcardYearlyPayout, xAxisCategories]
  );

  return (
    <div id="chart" className="w-full">
      <ReactApexChart
        options={chartData.options as ApexOptions}
        series={chartData.series}
        type="bar"
        height={360}
      />
    </div>
  );
};

export default WalletGraph;
